import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Check,
  Eye,
  EyeOff,
  Image as ImageIcon,
  Pencil,
  Plus,
  Rotate3D,
  Trash2,
  Upload,
  X,
} from 'lucide-react';

export interface VirtualTour {
  id: string;
  title: string;
  description: string | null;
  panoramaUrl: string;
  thumbnailUrl: string | null;
  displayOrder: number;
  isPublished: boolean;
}

interface VirtualTourForm {
  title: string;
  description: string;
  panoramaUrl: string;
  thumbnailUrl: string;
  panoramaFile: File | null;
  thumbnailFile: File | null;
  isPublished: boolean;
}

const BLANK_FORM: VirtualTourForm = {
  title: '',
  description: '',
  panoramaUrl: '',
  thumbnailUrl: '',
  panoramaFile: null,
  thumbnailFile: null,
  isPublished: true,
};

const normalizeTour = (tour: Record<string, unknown>): VirtualTour => ({
  id: String(tour.id),
  title: String(tour.title ?? ''),
  description: tour.description ? String(tour.description) : null,
  panoramaUrl: String(tour.panoramaUrl ?? tour.panorama_url ?? ''),
  thumbnailUrl: tour.thumbnailUrl || tour.thumbnail_url
    ? String(tour.thumbnailUrl ?? tour.thumbnail_url)
    : null,
  displayOrder: Number(tour.displayOrder ?? tour.display_order ?? 0),
  isPublished: Boolean(tour.isPublished ?? tour.is_published ?? true),
});

const slugify = (value: string) => value
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-|-$/g, '');

interface VirtualTourManagerProps {
  municipalityId: string;
}

export const VirtualTourManager = ({ municipalityId }: VirtualTourManagerProps) => {
  const { toast } = useToast();
  const [tours, setTours] = useState<VirtualTour[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<VirtualTourForm>(BLANK_FORM);

  const loadTours = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.virtualTours.listAdmin(municipalityId);
      setTours((data ?? []).map((tour) => normalizeTour(tour)));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'No se pudieron cargar las visitas';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [municipalityId, toast]);

  useEffect(() => {
    void loadTours();
  }, [loadTours]);

  const panoramaPreview = useMemo(() => {
    if (form.panoramaFile) return URL.createObjectURL(form.panoramaFile);
    return form.panoramaUrl;
  }, [form.panoramaFile, form.panoramaUrl]);

  useEffect(() => () => {
    if (form.panoramaFile && panoramaPreview.startsWith('blob:')) {
      URL.revokeObjectURL(panoramaPreview);
    }
  }, [form.panoramaFile, panoramaPreview]);

  const openCreate = () => {
    setForm(BLANK_FORM);
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (tour: VirtualTour) => {
    setForm({
      title: tour.title,
      description: tour.description ?? '',
      panoramaUrl: tour.panoramaUrl,
      thumbnailUrl: tour.thumbnailUrl ?? '',
      panoramaFile: null,
      thumbnailFile: null,
      isPublished: tour.isPublished,
    });
    setEditingId(tour.id);
    setShowForm(true);
  };

  const uploadFile = async (file: File, kind: 'panorama' | 'thumbnail') => {
    const extension = file.name.split('.').pop() || 'jpg';
    const name = slugify(form.title || 'visita-360');
    const path = `virtual-tours/${municipalityId}/${Date.now()}-${name}-${kind}.${extension}`;
    return api.storage.upload(file, path, 'images');
  };

  const handleSave = async () => {
    if (!form.title.trim() || (!form.panoramaFile && !form.panoramaUrl.trim())) {
      toast({
        title: 'Faltan datos',
        description: 'Indica un título y sube un panorama 360 o pega su URL.',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);
    try {
      let panoramaUrl = form.panoramaUrl.trim();
      let thumbnailUrl = form.thumbnailUrl.trim();

      if (form.panoramaFile) {
        panoramaUrl = (await uploadFile(form.panoramaFile, 'panorama')).url;
      }
      if (form.thumbnailFile) {
        thumbnailUrl = (await uploadFile(form.thumbnailFile, 'thumbnail')).url;
      }

      const payload = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        panorama_url: panoramaUrl,
        thumbnail_url: thumbnailUrl || panoramaUrl,
        display_order: editingId
          ? tours.find((tour) => tour.id === editingId)?.displayOrder ?? 0
          : tours.length,
        is_published: form.isPublished,
      };

      if (editingId) {
        await api.virtualTours.update(municipalityId, editingId, payload);
        toast({ title: 'Visita 360 actualizada' });
      } else {
        await api.virtualTours.create(municipalityId, payload);
        toast({ title: 'Visita 360 creada' });
      }

      setShowForm(false);
      setEditingId(null);
      setForm(BLANK_FORM);
      await loadTours();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'No se pudo guardar la visita';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tour: VirtualTour) => {
    if (!window.confirm(`¿Eliminar la visita 360 "${tour.title}"?`)) return;
    try {
      await api.virtualTours.delete(municipalityId, tour.id);
      toast({ title: 'Visita 360 eliminada' });
      await loadTours();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'No se pudo eliminar';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    }
  };

  const togglePublished = async (tour: VirtualTour) => {
    try {
      await api.virtualTours.update(municipalityId, tour.id, {
        is_published: !tour.isPublished,
      });
      await loadTours();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'No se pudo actualizar';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    }
  };

  return (
    <Card className="border-0 shadow-none">
      <CardHeader className="px-0 pt-2">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Rotate3D className="h-5 w-5 text-primary" />
              Visitas 360
              <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                {tours.length}
              </span>
            </CardTitle>
            <CardDescription className="mt-1">
              Sube fotografías panorámicas 2:1, añade una portada y decide cuáles se publican en el tótem.
            </CardDescription>
          </div>
          <Button type="button" onClick={openCreate} className="w-full sm:w-auto">
            <Plus className="mr-2 h-4 w-4" />
            Añadir visita 360
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-5 px-0">
        {showForm && (
          <div className="space-y-5 rounded-xl border bg-muted/15 p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold">{editingId ? 'Editar visita 360' : 'Nueva visita 360'}</p>
                <p className="text-xs text-muted-foreground">Recomendado: JPG/WebP panorámico de 6000×3000 px.</p>
              </div>
              <Button type="button" variant="ghost" size="icon" onClick={() => setShowForm(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Título *</Label>
                <Input
                  value={form.title}
                  onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
                  placeholder="Ej: Colegiata de San Martín de Elines"
                />
              </div>
              <div className="flex items-end">
                <div className="flex w-full items-center justify-between rounded-lg border bg-background px-3 py-2">
                  <div>
                    <Label htmlFor="tour-published">Visible en el tótem</Label>
                    <p className="text-xs text-muted-foreground">Puedes guardarla como borrador.</p>
                  </div>
                  <Switch
                    id="tour-published"
                    checked={form.isPublished}
                    onCheckedChange={(checked) => setForm((current) => ({ ...current, isPublished: checked }))}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Descripción</Label>
              <Textarea
                value={form.description}
                onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
                placeholder="Explica qué puede descubrir el visitante en esta vista."
                rows={3}
              />
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <div className="space-y-3 rounded-xl border bg-background p-4">
                <div className="flex items-center gap-2 font-medium">
                  <Rotate3D className="h-4 w-4 text-primary" /> Panorama 360 *
                </div>
                <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-6 text-center transition-colors hover:border-primary hover:bg-primary/5">
                  <Upload className="h-7 w-7 text-primary" />
                  <span className="text-sm font-medium">Seleccionar panorama</span>
                  <span className="text-xs text-muted-foreground">JPG, PNG o WebP · máximo 10 MB</span>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={(event) => {
                      const file = event.target.files?.[0] ?? null;
                      setForm((current) => ({ ...current, panoramaFile: file }));
                    }}
                  />
                </label>
                <div className="space-y-1">
                  <Label className="text-xs">O utiliza una URL existente</Label>
                  <Input
                    type="url"
                    value={form.panoramaUrl}
                    onChange={(event) => setForm((current) => ({ ...current, panoramaUrl: event.target.value }))}
                    placeholder="https://.../panorama.jpg"
                  />
                </div>
              </div>

              <div className="space-y-3 rounded-xl border bg-background p-4">
                <div className="flex items-center gap-2 font-medium">
                  <ImageIcon className="h-4 w-4 text-primary" /> Portada opcional
                </div>
                <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-6 text-center transition-colors hover:border-primary hover:bg-primary/5">
                  <Upload className="h-7 w-7 text-primary" />
                  <span className="text-sm font-medium">Seleccionar miniatura</span>
                  <span className="text-xs text-muted-foreground">Si no subes una, se usará el panorama.</span>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={(event) => {
                      const file = event.target.files?.[0] ?? null;
                      setForm((current) => ({ ...current, thumbnailFile: file }));
                    }}
                  />
                </label>
              </div>
            </div>

            {panoramaPreview && (
              <div
                className="relative h-40 overflow-hidden rounded-xl border bg-cover bg-center"
                style={{ backgroundImage: `url("${panoramaPreview}")` }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-3 left-3 flex items-center gap-2 text-sm font-medium text-white">
                  <Eye className="h-4 w-4" /> Vista previa del panorama
                </div>
              </div>
            )}

            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                Cancelar
              </Button>
              <Button type="button" onClick={handleSave} disabled={saving}>
                <Check className="mr-2 h-4 w-4" />
                {saving ? 'Guardando...' : 'Guardar visita'}
              </Button>
            </div>
          </div>
        )}

        {loading && <p className="py-8 text-center text-sm text-muted-foreground">Cargando visitas 360...</p>}

        {!loading && tours.length === 0 && !showForm && (
          <div className="rounded-xl border-2 border-dashed py-12 text-center">
            <Rotate3D className="mx-auto mb-3 h-10 w-10 text-muted-foreground/50" />
            <p className="font-medium">Todavía no hay visitas 360</p>
            <p className="mt-1 text-sm text-muted-foreground">Sube el primer panorama para activar esta sección.</p>
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-2">
          {tours.map((tour) => (
            <div key={tour.id} className="overflow-hidden rounded-xl border bg-background shadow-sm">
              <div
                className="relative h-40 bg-muted bg-cover bg-center"
                style={{ backgroundImage: `url("${tour.thumbnailUrl ?? tour.panoramaUrl}")` }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-black/55 px-2.5 py-1 text-xs font-semibold text-white backdrop-blur-sm">
                  <Rotate3D className="h-3.5 w-3.5" /> 360°
                </span>
                <span className={`absolute right-3 top-3 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${tour.isPublished ? 'bg-emerald-600 text-white' : 'bg-amber-100 text-amber-900'}`}>
                  {tour.isPublished ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                  {tour.isPublished ? 'Publicada' : 'Borrador'}
                </span>
                <p className="absolute bottom-3 left-3 right-3 truncate font-semibold text-white">{tour.title}</p>
              </div>
              <div className="flex items-center justify-between gap-2 p-3">
                <Button type="button" variant="ghost" size="sm" onClick={() => togglePublished(tour)}>
                  {tour.isPublished ? <EyeOff className="mr-1.5 h-4 w-4" /> : <Eye className="mr-1.5 h-4 w-4" />}
                  {tour.isPublished ? 'Ocultar' : 'Publicar'}
                </Button>
                <div className="flex gap-1">
                  <Button type="button" variant="ghost" size="icon" onClick={() => openEdit(tour)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button type="button" variant="ghost" size="icon" className="text-destructive" onClick={() => handleDelete(tour)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
